import { Controller } from "@hotwired/stimulus"

// Opens the white paper offer (the same content as the sidebar on the
// "what is RDM" page) in a modal dialog.
//
// The markup is fetched from Rails rather than baked into the page so that
// static pages get a CSRF token valid for the visitor's own session — a token
// rendered at static-generation time would be useless. It also keeps a single
// server-side source of truth for the offer, so an inline CTA anywhere (a blog
// post, a docs page) needs nothing but the trigger itself:
//
//   <button data-controller="whitepaper-modal" data-action="whitepaper-modal#open">
//     Send me the paper
//   </button>
//
// Registered in BOTH controllers/index.js and controllers/static_controllers.js
// so it works on dynamic and static pages alike.

const DEFAULT_URL = "/what-is-reference-data-management/form"
const FALLBACK_HTML =
  '<p class="whitepaper-modal__status">Sorry, the form could not be loaded. ' +
  '<a href="/what-is-reference-data-management" class="underline">Open the full page instead</a>.</p>'

// One dialog and one fetched copy of the offer are shared by every trigger on
// the page, so several CTAs in a long blog post cost nothing extra.
let dialog = null
let cachedHtml = null

export default class extends Controller {
  static values = { url: { type: String, default: DEFAULT_URL } }

  async open(event) {
    if (event) event.preventDefault()

    const modal = this.dialog()
    this.setBody('<p class="whitepaper-modal__status">Loading…</p>')
    modal.showModal()

    this.setBody(await this.offerHtml())
    // The fetched markup is cached and reused across every page the modal is
    // opened on, so the attribution hidden fields baked in at fetch time
    // reflect the fetch endpoint, not the page the visitor is actually on.
    // Refresh them from the browser's own state on every open.
    this.refreshAttribution(modal)
    // Focusing the first field makes the modal immediately usable and gives
    // screen readers a sensible starting point inside the dialog.
    modal.querySelector("input:not([type=hidden]):not([tabindex='-1'])")?.focus()
  }

  refreshAttribution(modal) {
    const landingPage = modal.querySelector('input[name="landing_page"]')
    if (landingPage) landingPage.value = window.location.pathname

    const gclid = modal.querySelector('input[name="gclid"]')
    if (gclid) gclid.value = new URLSearchParams(window.location.search).get("gclid") || this.readCookie("gclid") || ""
  }

  readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  }

  async offerHtml() {
    if (cachedHtml) return cachedHtml

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "text/html" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`Unexpected ${response.status}`)
      cachedHtml = await response.text()
    } catch (error) {
      console.error("Could not load the white paper form:", error)
      return FALLBACK_HTML
    }

    return cachedHtml
  }

  setBody(html) {
    this.dialog().querySelector(".whitepaper-modal__body").innerHTML = html
  }

  // Built lazily, and rebuilt if a Turbo visit has replaced the body it lived in.
  dialog() {
    if (dialog?.isConnected) return dialog

    dialog = document.createElement("dialog")
    dialog.className = "whitepaper-modal"
    dialog.setAttribute("aria-label", "Get the free white paper")
    dialog.innerHTML =
      '<button type="button" class="whitepaper-modal__close" aria-label="Close">&times;</button>' +
      '<div class="whitepaper-modal__body"></div>'

    dialog.querySelector(".whitepaper-modal__close").addEventListener("click", () => dialog.close())
    // Clicks land on the dialog itself only when they hit the backdrop.
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close()
    })

    document.body.appendChild(dialog)
    return dialog
  }
}
